import React, { useState } from 'react'
import { Box, Chip, Tooltip, Typography } from '@mui/material'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked'
import ErrorIcon from '@mui/icons-material/Error'
import PriorityHighIcon from '@mui/icons-material/PriorityHigh'
import EditIcon from '@mui/icons-material/Edit'
import HelpOutlineIcon from '@mui/icons-material/HelpOutline'
import type { FieldMappingDto } from '@app/src/types/tabularImport'
import { getFieldConfig } from '../fieldConfig'
import { useTabularImportEntity } from '../TabularImportEntityContext'
import { describeExtractionNode, getSourceColumns } from '../mappingLogic'
import { useIntl } from 'react-intl'
import MappingOverridePopover from './MappingOverridePopover'

export type OverrideExtrasRenderer = (args: {
  mapping: FieldMappingDto
  sourceColumns: string[]
  close: () => void
}) => React.ReactNode

type FieldToggleChipsProps = {
  mappings: FieldMappingDto[]
  missingRequiredFields: string[]
  onToggle: (mappingId: number) => void
  onRetarget: (mappingId: number, targetField: string) => void
  onSplit: (mappingId: number) => void
  unassignedHint: string
  renderOverrideExtras?: OverrideExtrasRenderer
}

const FieldToggleChips: React.FC<FieldToggleChipsProps> = ({
  mappings,
  missingRequiredFields,
  onToggle,
  onRetarget,
  onSplit,
  unassignedHint,
  renderOverrideExtras,
}) => {
  const { formatMessage } = useIntl()
  const { fieldConfigs, requiredFields } = useTabularImportEntity()
  const isRequired = (fieldName: string) => requiredFields.includes(fieldName)
  const isMissingRequired = (fieldName: string) => missingRequiredFields.includes(fieldName)

  // Retarget targets are real fields only; columns split out of a merge reuse their
  // header as a placeholder target and must not appear as a destination.
  const fieldOptions = mappings.map(m => m.targetFieldName).filter(name => getFieldConfig(fieldConfigs, name))
  const [overrideAnchor, setOverrideAnchor] = useState<HTMLElement | null>(null)
  const [overrideMapping, setOverrideMapping] = useState<FieldMappingDto | null>(null)
  const closeOverride = () => {
    setOverrideAnchor(null)
    setOverrideMapping(null)
  }

  return (
    <Box display="flex" flexWrap="wrap" gap={1}>
      {mappings.map(mapping => {
        const required = isRequired(mapping.targetFieldName)
        const missingReq = isMissingRequired(mapping.targetFieldName)
        const label = `${mapping.targetFieldName}${required ? ' *' : ''}`

        if (!mapping.extractionTree) {
          if (!missingReq) return null

          const tooltipContent = (
            <Box>
              <Typography variant="body2">No matching column found</Typography>
              {mapping.mappingReason && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  {mapping.mappingReason}
                </Typography>
              )}
            </Box>
          )

          return (
            <Tooltip key={mapping.id} title={tooltipContent} arrow placement="top">
              <Chip
                icon={<ErrorIcon />}
                label={label}
                color="error"
                variant="outlined"
                sx={{ cursor: 'default', borderStyle: 'dashed' }}
              />
            </Tooltip>
          )
        }

        // A column split out of a merge reuses its header as a target name, so it isn't a
        // real entity field yet. Show it as a dashed "needs assigning" chip whose pencil
        // opens the same override popover.
        const isUnassignedColumn = !getFieldConfig(fieldConfigs, mapping.targetFieldName)
        if (isUnassignedColumn) {
          return (
            <Tooltip key={mapping.id} title={unassignedHint} arrow placement="top">
              <Chip
                icon={<HelpOutlineIcon />}
                label={mapping.targetFieldName}
                variant="outlined"
                onDelete={e => {
                  setOverrideAnchor(e.currentTarget)
                  setOverrideMapping(mapping)
                }}
                deleteIcon={
                  <Tooltip title="Assign column">
                    <EditIcon fontSize="small" />
                  </Tooltip>
                }
                sx={{ cursor: 'default', borderStyle: 'dashed' }}
              />
            </Tooltip>
          )
        }

        const confidencePercent = Math.round(mapping.confidenceScore * 100)
        const tooltipContent = (
          <Box>
            <Typography variant="body2">
              {describeExtractionNode(mapping.extractionTree, (desc, values) => formatMessage(desc, values))}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Confidence: {confidencePercent}%
            </Typography>
            {mapping.mappingReason && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                {mapping.mappingReason}
              </Typography>
            )}
          </Box>
        )
        const lowConfidence = mapping.confidenceScore < 0.9
        const chipIcon = missingReq ? (
          <ErrorIcon />
        ) : lowConfidence && mapping.isApplied ? (
          <PriorityHighIcon sx={{ color: '#b71c1c' }} />
        ) : mapping.isApplied ? (
          <CheckCircleIcon />
        ) : (
          <RadioButtonUncheckedIcon />
        )
        const chipColor = missingReq ? 'error' : mapping.isApplied ? 'primary' : 'default'
        const isToggleable = !required && !missingReq

        const lowConfidenceSx =
          lowConfidence && !missingReq
            ? mapping.isApplied
              ? { '& .MuiChip-icon': { color: '#b71c1c' } }
              : {
                  bgcolor: 'rgba(239, 83, 80, 0.08)',
                  color: 'rgba(239, 83, 80, 0.7)',
                  '& .MuiChip-icon': { color: 'rgba(239, 83, 80, 0.7)' },
                }
            : {}

        return (
          <Tooltip key={mapping.id} title={tooltipContent} arrow placement="top">
            <Chip
              icon={chipIcon}
              label={label}
              color={chipColor}
              variant="filled"
              onClick={isToggleable ? () => onToggle(mapping.id) : undefined}
              onDelete={e => {
                setOverrideAnchor(e.currentTarget)
                setOverrideMapping(mapping)
              }}
              deleteIcon={
                <Tooltip title="Change mapping">
                  <EditIcon fontSize="small" />
                </Tooltip>
              }
              sx={{ cursor: isToggleable ? 'pointer' : 'default', ...lowConfidenceSx }}
            />
          </Tooltip>
        )
      })}
      {overrideMapping && (
        <MappingOverridePopover
          anchorEl={overrideAnchor}
          open={Boolean(overrideAnchor)}
          mapping={overrideMapping}
          fieldOptions={fieldOptions}
          mappedTargetFields={mappings
            .filter(m => m.extractionTree && m.id !== overrideMapping.id)
            .map(m => m.targetFieldName)}
          sourceColumns={getSourceColumns(overrideMapping.extractionTree)}
          isUnassigned={!getFieldConfig(fieldConfigs, overrideMapping.targetFieldName)}
          isRequired={isRequired(overrideMapping.targetFieldName)}
          unassignedHint={unassignedHint}
          extras={renderOverrideExtras?.({
            mapping: overrideMapping,
            sourceColumns: getSourceColumns(overrideMapping.extractionTree),
            close: closeOverride,
          })}
          onClose={closeOverride}
          onRetarget={targetField => {
            onRetarget(overrideMapping.id, targetField)
            closeOverride()
          }}
          onSplit={() => {
            onSplit(overrideMapping.id)
            closeOverride()
          }}
          onToggleApplied={() => {
            onToggle(overrideMapping.id)
            closeOverride()
          }}
        />
      )}
    </Box>
  )
}

export default FieldToggleChips
